import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  FiArrowLeft,
  FiMonitor,
  FiSmartphone,
  FiSave,
  FiEye,
} from "react-icons/fi";
import TextEditor from "../../../components/textEditor";
import CustomInputField from "../../../components/customInputField";
import ModalV2 from "../../../components/customModal/ModalV2";
import "../style.css";
import { emailTemplateValidationSchema } from "../../../utils/validationSchema";
import { GET_API_CALL, PUT_API_CALL } from "../../../utils/services";
import { ENDPOINTS } from "../../../utils/endPoints";
import { EMAIL_STATUS } from "../../../utils/contentTypes";
import { formatSlugToLabel } from "../../../utils/commonFunctions";
import { MdAdd, MdRestore } from "react-icons/md";
import toast from "react-hot-toast";
import EmailTemplateDetailsSkeleton from "./EmailSkeleton";

const MOBILE_FRAME_WIDTH = 375;
const DESKTOP_CONTENT_WIDTH = 900;

const humanizeVariableKey = (key) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const extractTemplateVariables = (html = "") => {
  const seen = new Set();
  const variables = [];
  const pattern = /\{\{\s*([a-zA-Z_][\w]*)\s*\}\}/g;
  let match;

  while ((match = pattern.exec(html))) {
    const key = match[1];
    if (seen.has(key)) continue;
    seen.add(key);
    variables.push({
      key,
      token: `{{${key}}}`,
      label: humanizeVariableKey(key),
    });
  }

  return variables;
};

const EmailTemplateDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const iframeRef = useRef(null);
  const editorRef = useRef(null);
  const [state, setState] = useState({
    _id: "",
    type: "",
    subject: "",
    status: "",
    html: "",
    defaultHtml: "",
    createdAt: "",
    updatedAt: "",
  });
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [mobileScaledHeight, setMobileScaledHeight] = useState(600);

  function getEmailListing() {
    setLoading(true);
    GET_API_CALL(ENDPOINTS.EMAIL.DETAILS, id, (res) => {
      console.log("response in email details :", res);
      if (res.isSuccess) {
        const {
          _id,
          type,
          subject,
          status,
          html,
          defaultHtml,
          createdAt,
          updatedAt,
        } = res.data;

        setState({
          _id,
          type,
          subject,
          status,
          html,
          defaultHtml,
          createdAt,
          updatedAt,
        });
      } else {
        console.log("Error in email listing :", res);
      }
      setLoading(false);
    });
  }

  useEffect(() => {
    getEmailListing();
  }, []);

  const { html, type, status, subject } = state || {};
  const templateVariables = useMemo(
    () => extractTemplateVariables(state.defaultHtml),
    [state.defaultHtml]
  );

  const scale = MOBILE_FRAME_WIDTH / DESKTOP_CONTENT_WIDTH;

  const handleIframeLoad = () => {
    const iframeDoc = iframeRef.current?.contentDocument;
    if (iframeDoc) {
      const contentHeight = iframeDoc.documentElement.scrollHeight;
      setMobileScaledHeight(contentHeight * scale);
    }
  };

  const insertEmailVariable = (variable) => {
    const inserted = editorRef.current?.insertAtCursor(variable);
    if (!inserted) {
      toast.error("Place your cursor in the email content, then add a variable");
    }
  };

  const initialValues = {
    type: type || "",
    subject: subject || "",
    status: status || "active",
    html: html || "",
  };

  const handleSubmit = (values, { setSubmitting }) => {
    const toastId = toast.loading("Saving details");
    const payload = {
      subject: values.subject.trim(),
      status: values.status,
      html: values.html,
    };

    PUT_API_CALL(ENDPOINTS.EMAIL.EDIT(id), payload, (res) => {
      console.log("res :", res);
      toast.success(res.data.message);
      setSubmitting(false);
      toast.dismiss(toastId);
    });
  };

  if (loading) {
    return <EmailTemplateDetailsSkeleton />;
  }
  return (
    <div className="etd-page">
      <Formik
        initialValues={initialValues}
        validationSchema={emailTemplateValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          dirty,
        }) => (
          <>
            <Form className="etd-layout">
              {/* ---------------- Left: Editor Panel (2/3) ---------------- */}
              <div className="etd-editor-panel">
                <div className="etd-card etd-editor-card">
                  <div className="etd-editor-card-header">
                    <h4 className="etd-card-title mb-0">Email Content</h4>
                    <div className="flex gap-sm">
                      <button
                        type="button"
                        className="etd-btn etd-btn-reset"
                        onClick={() => setFieldValue("html", state.defaultHtml)}
                      >
                        <MdRestore />
                        <span>Reset</span>
                      </button>
                      <button
                        type="button"
                        className="etd-btn etd-btn-preview"
                        onClick={() => setPreviewModalOpen(true)}
                      >
                        <FiEye />
                        <span>Preview</span>
                      </button>
                    </div>
                  </div>
                  <TextEditor
                    ref={editorRef}
                    value={values.html}
                    onChange={(e) => setFieldValue("html", e.target.value)}
                  />
                  {touched.html && errors.html && (
                    <div className="etd-error">{errors.html}</div>
                  )}
                </div>
              </div>

              {/* ---------------- Right: Template Info Panel (1/3) ---------------- */}
              <div className="etd-info-panel-wrapper">
                <div className="etd-info-panel">
                  <div className="etd-card">
                    <h4 className="etd-card-title">Template Info</h4>

                    <div className="etd-field">
                      <CustomInputField
                        changeHandler={handleChange}
                        onBlur={handleBlur}
                        label="Template Type"
                        name="type"
                        placeholder="Enter template name"
                        value={formatSlugToLabel(values.type)}
                        type="text"
                        disabledPropery
                      />
                      {touched.type && errors.type && (
                        <div className="etd-error">{errors.type}</div>
                      )}
                    </div>

                    <div className="etd-field">
                      <CustomInputField
                        changeHandler={handleChange}
                        onBlur={handleBlur}
                        label="Subject"
                        name="subject"
                        placeholder="Enter subject"
                        value={values.subject}
                        type="text"
                      />
                      {touched.subject && errors.subject && (
                        <div className="etd-error">{errors.subject}</div>
                      )}
                    </div>

                    <div className="etd-field">
                      <label className="etd-label">Status</label>
                      <select
                        className="etd-select"
                        value={values.status}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        name="status"
                      >
                        {Object.values(EMAIL_STATUS).map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                      {touched.status && errors.status && (
                        <div className="etd-error">{errors.status}</div>
                      )}
                    </div>

                    {/* email variables */}
                    <div className="etd-field etd-variables">
                      <label className="etd-label">Template Variables</label>
                      <p className="etd-variables-hint">
                        Click + to insert a variable at the current cursor
                        position in the email content.
                      </p>
                      <div className="etd-variable-list">
                        {templateVariables.length ? (
                          templateVariables.map(({ key, token, label }) => (
                            <div key={key} className="etd-variable-row">
                              <div className="etd-variable-copy">
                                <span className="etd-variable-name">
                                  {label}
                                </span>
                                <code className="etd-variable-token">
                                  {token}
                                </code>
                              </div>
                              <button
                                type="button"
                                className="etd-variable-add"
                                title={`Insert ${token}`}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => insertEmailVariable(token)}
                              >
                                <MdAdd />
                              </button>
                            </div>
                          ))
                        ) : (
                          <p className="etd-variables-hint">
                            No template variables are available for this email.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ---------------- Sticky Save Bar ---------------- */}
                <div className="etd-action-bar">
                  <button
                    type="button"
                    className="etd-btn etd-btn-secondary"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="etd-btn etd-btn-primary"
                    disabled={isSubmitting || !dirty}
                  >
                    <FiSave />
                    <span>{isSubmitting ? "Saving..." : "Save Changes"}</span>
                  </button>
                </div>
              </div>
            </Form>

            {/* ---------------- Preview Modal ---------------- */}
            <ModalV2
              isOpen={previewModalOpen}
              onClose={() => setPreviewModalOpen(false)}
              onConfirm={() => setPreviewModalOpen(false)}
              title="Email Preview"
              cancelButtonLabel="Close"
              confirmButtonLabel="Done"
              size="large"
            >
              <div className="etd-preview-modal-body">
                <div className="etd-preview-header">
                  <div className="etd-preview-toggle">
                    <button
                      type="button"
                      className={`etd-toggle-btn ${
                        previewMode === "desktop" ? "active" : ""
                      }`}
                      onClick={() => setPreviewMode("desktop")}
                    >
                      <FiMonitor />
                      <span>Desktop</span>
                    </button>
                    {/* <button
                      type="button"
                      className={`etd-toggle-btn ${
                        previewMode === "mobile" ? "active" : ""
                      }`}
                      onClick={() => setPreviewMode("mobile")}
                    >
                      <FiSmartphone />
                      <span>Mobile</span>
                    </button> */}
                  </div>
                </div>

                <div className="etd-preview-subject">
                  <span className="etd-preview-subject-label">Subject:</span>{" "}
                  {values.subject || "No subject"}
                </div>

                {/* <div className="etd-preview-frame-wrapper">
                  <div
                    className={`etd-preview-frame-shell ${
                      previewMode === "mobile" ? "mobile" : "desktop"
                    }`}
                  >
                    <iframe
                      title="email-preview"
                      className="etd-preview-iframe"
                      srcDoc={values.html}
                      sandbox=""
                    />
                  </div>
                </div> */}

                <div className="etd-preview-frame-wrapper">
                  <div
                    className={`etd-preview-frame-shell ${
                      previewMode === "mobile" ? "mobile" : "desktop"
                    }`}
                    style={
                      previewMode === "mobile"
                        ? { height: mobileScaledHeight }
                        : undefined
                    }
                  >
                    {previewMode === "mobile" ? (
                      <div
                        className="etd-preview-mobile-scale-wrapper"
                        style={{ height: mobileScaledHeight }}
                      >
                        <iframe
                          ref={iframeRef}
                          title="email-preview"
                          className="etd-preview-iframe etd-preview-iframe-scaled"
                          srcDoc={values.html}
                          sandbox=""
                          onLoad={handleIframeLoad}
                          style={{
                            width: DESKTOP_CONTENT_WIDTH,
                            transform: `scale(${scale})`,
                          }}
                        />
                      </div>
                    ) : (
                      <iframe
                        title="email-preview"
                        className="etd-preview-iframe"
                        srcDoc={values.html}
                        sandbox=""
                      />
                    )}
                  </div>
                </div>
              </div>
            </ModalV2>
          </>
        )}
      </Formik>
    </div>
  );
};

export default EmailTemplateDetails;
